import React, { FC, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { useFlag, useFeatureFlagJson } from '@modernloop/shared/feature-flag';

import AppWideBanner, { AppWideBannerAction } from '../AppWideBanner';

import { selectVisibleStatusPageItems } from './selectVisibleStatusPageItems';
import { StatusPageBannerConfig, StatusPageBannerItem } from './types';
import { useStatusPageSummary } from './useStatusPageSummary';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function getBannerText(item: StatusPageBannerItem, additionalCount: number, t: TranslateFn): string {
  const baseText = item.item.name;
  if (additionalCount <= 0) {
    return baseText;
  }

  return t('hiring-dashboard-nav:nav.banner.statusPageAdditionalItems', {
    name: baseText,
    count: additionalCount,
  });
}

function getBannerTheme(item: StatusPageBannerItem): {
  backgroundColor: 'error' | 'warning';
  color: 'foreground';
} {
  if (item.type === 'MAINTENANCE') {
    return { backgroundColor: 'warning', color: 'foreground' };
  }

  return { backgroundColor: 'error', color: 'foreground' };
}

/**
 * Renders a LaunchDarkly-gated status banner backed by the public Statuspage API instead of the
 * global Statuspage embed script.
 */
const StatusPageBanner: FC = () => {
  const { t } = useTranslation();
  const showStatusPageBanner = useFlag('status_page_banner');
  const statusPageBannerConfig = useFeatureFlagJson('status-page-banner') as StatusPageBannerConfig | null;
  const { data, isLoading } = useStatusPageSummary(showStatusPageBanner);

  const visibleItems = useMemo(() => {
    if (!data) {
      return [];
    }
    return selectVisibleStatusPageItems(data, statusPageBannerConfig);
  }, [data, statusPageBannerConfig]);

  if (!showStatusPageBanner || isLoading || visibleItems.length === 0) {
    return null;
  }

  const [primaryItem, ...remainingItems] = visibleItems;
  const action: AppWideBannerAction = {
    label: t('hiring-dashboard-nav:nav.banner.statusPageViewStatus'),
    href: primaryItem.item.shortlink,
  };

  return (
    <AppWideBanner
      key={primaryItem.item.id}
      {...getBannerTheme(primaryItem)}
      text={getBannerText(primaryItem, remainingItems.length, t)}
      action={action}
      showClose
    />
  );
};

export default StatusPageBanner;
